import {
  CutsheetCapabilityOptionFragmentFragment,
  CutsheetCapabilitySpecificationFragmentFragment,
  CutsheetQuestionnaireFragmentFragment,
} from 'lib/graphql';

import type { PricedItem, PricedSpec, YieldSource } from './types';

interface PricingOverride {
  model: PricedItem['model'];
  price: number;
  minimumCharge?: number;
  description?: string;
}

/**
 * Resolves the effective pricing for a V2 capability option — the
 * questionnaire's override (if present) takes precedence over the
 * option's own pricing.
 * @param optionId
 * @param option
 * @param pricingOverrides
 */
function resolveV2OptionPricing(
  optionId: string,
  option: CutsheetCapabilityOptionFragmentFragment | undefined,
  pricingOverrides: Record<string, PricingOverride> | null,
): PricingOverride | null {
  const override = pricingOverrides?.[optionId];
  if (override && override.model && typeof override.price === 'number') {
    return override;
  }
  const p = option?.pricing;
  if (p && p.model && typeof p.price === 'number') {
    return {
      model: p.model as PricedItem['model'],
      price: p.price,
      minimumCharge: p.minimumCharge ?? undefined,
      description: p.description ?? undefined,
    };
  }
  return null;
}

interface BuildV1Args {
  extras:
    | Array<{
        name?: string | null;
        pricePerPound?: number | null;
        quantity?: number | null;
        type?: string | null;
        fee?: number | null;
      } | null>
    | null
    | undefined;
  trim:
    | Array<{
        name?: string | null;
        pricePerPound?: number | null;
        quantity?: number | null;
        trimSelection?: string | null;
      } | null>
    | null
    | undefined;
  cutsheet:
    | {
        name?: string | null;
        pricePerPound?: number | null;
      }
    | null
    | undefined;
  /**
   * When the cutsheet is a "custom" (user) cutsheet, we substitute the
   * processor's customCutsheet rate for the cutsheet's pricePerPound.
   */
  customCutsheetRate: number | null;
  isCustomCutsheet: boolean;
}

export function buildV1PricedItems(args: BuildV1Args): PricedItem[] {
  const { extras, trim, cutsheet, customCutsheetRate, isCustomCutsheet } = args;
  const items: PricedItem[] = [];

  const cutsheetPrice = isCustomCutsheet
    ? (customCutsheetRate ?? 0)
    : (cutsheet?.pricePerPound ?? 0);
  if (cutsheetPrice > 0) {
    items.push({
      key: 'v1-cutsheet',
      label: cutsheet?.name ? `Cutsheet — ${cutsheet.name}` : 'Cutsheet',
      model: 'per_hanging_weight',
      price: cutsheetPrice,
      invoiceName: 'Cutsheet',
      source: 'v1-cutsheet',
    });
  }

  for (const extra of extras ?? []) {
    if (!extra) continue;
    const name = extra.name ?? 'Extra';
    if (extra.type === 'pricedPerPound') {
      const pricePerPound = extra.pricePerPound ?? 0;
      if (pricePerPound > 0) {
        items.push({
          key: `v1-extra-ppp-${name}`,
          label: name,
          model: 'per_pound',
          price: pricePerPound,
          defaultQuantity: extra.quantity ?? undefined,
          invoiceName: 'Extra',
          source: 'v1-extra',
        });
      }
    } else {
      const fee = extra.fee ?? 0;
      if (fee > 0) {
        items.push({
          key: `v1-extra-fee-${name}`,
          label: name,
          model: 'fixed_price',
          price: fee,
          defaultQuantity: 1,
          invoiceName: 'Extra',
          source: 'v1-extra',
        });
      }
    }
  }

  for (const t of trim ?? []) {
    if (!t) continue;
    const pricePerPound = t.pricePerPound ?? 0;
    if (pricePerPound > 0) {
      items.push({
        key: `v1-trim-${t.name}`,
        label: t.name ?? 'Trim',
        model: 'per_pound',
        price: pricePerPound,
        defaultQuantity: t.quantity ?? undefined,
        invoiceName: 'Trim',
        source: 'v1-trim',
      });
    }
  }

  return items;
}

interface BuildV2Args {
  response: {
    pricePerPound?: number | null;
    selectedCuts?:
      | Array<{ optionId: string } | null | undefined>
      | null
      | undefined;
    trimSelections?: {
      trim?:
        | Array<
            | {
                optionId: string;
                quantity?: number | null;
                percentage?: number | null;
                rank?: number | null;
              }
            | null
            | undefined
          >
        | null
        | undefined;
    } | null;
    extraSelections?: {
      extras?:
        | Array<
            { optionId: string; quantity?: number | null } | null | undefined
          >
        | null
        | undefined;
    } | null;
  };
  /** Map of optionId → capability option. Undefined entries mean "not yet loaded". */
  optionsById: Map<string, CutsheetCapabilityOptionFragmentFragment>;
  /** Questionnaire with its pricingOverrides (JSON map optionId → PricingOverride). */
  questionnaire?: CutsheetQuestionnaireFragmentFragment | null;
}

export function buildV2PricedItems(args: BuildV2Args): PricedItem[] {
  const { response, optionsById, questionnaire } = args;
  const items: PricedItem[] = [];

  const overrides =
    (questionnaire?.pricingOverrides as Record<
      string,
      PricingOverride
    > | null) ?? null;

  // Base cutsheet price from the response
  const responsePpp = response.pricePerPound ?? 0;
  if (responsePpp > 0) {
    items.push({
      key: 'v2-cutsheet',
      label: 'Cutsheet',
      model: 'per_hanging_weight',
      price: responsePpp,
      invoiceName: 'Cutsheet',
      source: 'v2-cutsheet',
    });
  }

  for (const cut of response.selectedCuts ?? []) {
    if (!cut?.optionId) continue;
    const option = optionsById.get(cut.optionId);
    const pricing = resolveV2OptionPricing(cut.optionId, option, overrides);
    if (!pricing || pricing.price <= 0) continue;
    items.push({
      key: `v2-cut-${cut.optionId}`,
      label: option?.title ?? 'Cut',
      model: pricing.model,
      price: pricing.price,
      minimumCharge: pricing.minimumCharge,
      defaultQuantity: pricing.model === 'per_item' ? 1 : undefined,
      invoiceName: 'Cutsheet',
      source: 'v2-cut',
    });
  }

  for (const t of response.trimSelections?.trim ?? []) {
    if (!t?.optionId) continue;
    const option = optionsById.get(t.optionId);
    const pricing = resolveV2OptionPricing(t.optionId, option, overrides);
    if (!pricing || pricing.price <= 0) continue;
    items.push({
      key: `v2-trim-${t.optionId}`,
      label: option?.title ?? 'Trim',
      model: pricing.model,
      price: pricing.price,
      minimumCharge: pricing.minimumCharge,
      defaultQuantity:
        pricing.model === 'per_pound' ? (t.quantity ?? undefined) : undefined,
      invoiceName: 'Trim',
      source: 'v2-trim',
    });
  }

  for (const e of response.extraSelections?.extras ?? []) {
    if (!e?.optionId) continue;
    const option = optionsById.get(e.optionId);
    const pricing = resolveV2OptionPricing(e.optionId, option, overrides);
    if (!pricing || pricing.price <= 0) continue;
    items.push({
      key: `v2-extra-${e.optionId}`,
      label: option?.title ?? 'Extra',
      model: pricing.model,
      price: pricing.price,
      minimumCharge: pricing.minimumCharge,
      defaultQuantity:
        pricing.model === 'per_pound'
          ? (e.quantity ?? undefined)
          : pricing.model === 'per_item' || pricing.model === 'fixed_price'
            ? 1
            : undefined,
      invoiceName: 'Extra',
      source: 'v2-extra',
    });
  }

  return items;
}

interface BuildV1YieldSourcesArgs {
  primalCuts:
    | Array<{ name?: string | null } | null | undefined>
    | null
    | undefined;
  extras:
    | Array<
        | {
            name?: string | null;
            pricePerPound?: number | null;
            type?: string | null;
            fee?: number | null;
          }
        | null
        | undefined
      >
    | null
    | undefined;
  trim:
    | Array<
        | { name?: string | null; pricePerPound?: number | null }
        | null
        | undefined
      >
    | null
    | undefined;
}

/**
 * Builds the full list of cuts/extras/trims from a V1 cutsheet, regardless
 * of pricing. Used to seed the yield-tracking modal so a free cut still
 * shows up as a yield row.
 * @param args
 */
export function buildV1YieldSources(
  args: BuildV1YieldSourcesArgs,
): YieldSource[] {
  const sources: YieldSource[] = [];
  // V1 primal cuts roll into the flat cutsheet rate — no per-cut line item.
  for (const cut of args.primalCuts ?? []) {
    if (!cut?.name) continue;
    sources.push({ label: cut.name });
  }
  for (const extra of args.extras ?? []) {
    if (!extra?.name) continue;
    let pricing: YieldSource['pricing'];
    if (extra.type === 'pricedPerPound') {
      const ppp = extra.pricePerPound ?? 0;
      if (ppp > 0) pricing = { model: 'per_pound', price: ppp };
    } else {
      const fee = extra.fee ?? 0;
      if (fee > 0) pricing = { model: 'fixed_price', price: fee };
    }
    sources.push({ label: extra.name, pricing });
  }
  for (const t of args.trim ?? []) {
    if (!t?.name) continue;
    const ppp = t.pricePerPound ?? 0;
    sources.push({
      label: t.name,
      pricing: ppp > 0 ? { model: 'per_pound', price: ppp } : undefined,
    });
  }
  return sources;
}

interface BuildV2PricedSpecsArgs {
  response: {
    selectedCuts?:
      | Array<
          | {
              optionId: string;
              specifications?: Array<
                | {
                    specification: string;
                    value: string | number | string[] | null;
                  }
                | null
                | undefined
              > | null;
            }
          | null
          | undefined
        >
      | null
      | undefined;
    trimSelections?: {
      trim?:
        | Array<
            | {
                optionId: string;
                specifications?: Array<
                  | {
                      specification: string;
                      value: string | number | string[] | null;
                    }
                  | null
                  | undefined
                > | null;
              }
            | null
            | undefined
          >
        | null
        | undefined;
    } | null;
    extraSelections?: {
      extras?:
        | Array<
            | {
                optionId: string;
                specifications?: Array<
                  | {
                      specification: string;
                      value: string | number | string[] | null;
                    }
                  | null
                  | undefined
                > | null;
              }
            | null
            | undefined
          >
        | null
        | undefined;
    } | null;
  };
  optionsById: Map<string, CutsheetCapabilityOptionFragmentFragment>;
  specsById: Map<string, CutsheetCapabilitySpecificationFragmentFragment>;
}

interface ItemPricingObj {
  model: PricedItem['model'];
  price: number;
}

function isValidPricingModel(model: unknown): model is PricedItem['model'] {
  return (
    model === 'per_item' ||
    model === 'per_pound' ||
    model === 'per_hanging_weight' ||
    model === 'fixed_price'
  );
}

function coercePricing(entry: unknown): ItemPricingObj | null {
  if (!entry || typeof entry !== 'object') return null;
  const obj = entry as { model?: unknown; price?: unknown };
  if (typeof obj.price !== 'number' || obj.price <= 0) return null;
  if (!isValidPricingModel(obj.model)) return null;
  return { model: obj.model, price: obj.price };
}

/**
 * Pulls a `{model, price}` pricing entry out of `pricingAdjustments[valueId]`.
 * @param pricingAdjustments
 * @param valueId
 */
function readSpecAdjustment(
  pricingAdjustments: unknown,
  valueId: string,
): ItemPricingObj | null {
  if (!pricingAdjustments || typeof pricingAdjustments !== 'object')
    return null;
  return coercePricing(
    (pricingAdjustments as Record<string, unknown>)[valueId],
  );
}

/**
 * Looks up `pricing` directly on the spec's options array — used when
 * `options` is `SpecificationOptionModel[]` (each entry has `value` and an
 * optional `pricing`) rather than a plain `string[]`.
 * @param options
 * @param valueId
 */
function readSpecOptionPricing(
  options: unknown,
  valueId: string,
): ItemPricingObj | null {
  if (!Array.isArray(options)) return null;
  for (const opt of options) {
    if (opt && typeof opt === 'object') {
      const o = opt as { value?: unknown; pricing?: unknown };
      if (o.value === valueId) return coercePricing(o.pricing);
    }
  }
  return null;
}

function readSpecOptionLabel(
  options: unknown,
  valueId: string,
): string | undefined {
  if (!Array.isArray(options)) return undefined;
  for (const opt of options) {
    if (typeof opt === 'string') {
      if (opt === valueId) return opt;
    } else if (opt && typeof opt === 'object') {
      const o = opt as { value?: unknown; label?: unknown };
      if (o.value === valueId)
        return typeof o.label === 'string' ? o.label : valueId;
    }
  }
  return undefined;
}

/**
 * Builds the priced-spec adjustments grouped by their parent cut/extra/trim
 * option ID. Each spec answer with a `pricingAdjustments[value]` entry produces
 * one `PricedSpec`; multiselect answers produce one per selected value.
 * @param args
 */
export function buildV2PricedSpecs(
  args: BuildV2PricedSpecsArgs,
): Record<string, PricedSpec[]> {
  const { response, optionsById, specsById } = args;
  const result: Record<string, PricedSpec[]> = {};

  const collect = (
    parent:
      | {
          optionId: string;
          specifications?: Array<
            | {
                specification: string;
                value: string | number | string[] | null;
              }
            | null
            | undefined
          > | null;
        }
      | null
      | undefined,
    invoiceName: PricedSpec['invoiceName'],
  ) => {
    if (!parent?.optionId) return;
    const parentOption = optionsById.get(parent.optionId);
    const parentLabel = parentOption?.title ?? '';
    const specs: PricedSpec[] = [];
    for (const ans of parent.specifications ?? []) {
      if (!ans?.specification) continue;
      const specDef = specsById.get(ans.specification);
      if (!specDef?.pricingAdjustments) continue;
      const valueIds = Array.isArray(ans.value)
        ? ans.value
        : ans.value != null
          ? [ans.value]
          : [];
      for (const v of valueIds) {
        const valueId = String(v);
        const pricing =
          readSpecAdjustment(specDef.pricingAdjustments, valueId) ??
          readSpecOptionPricing(specDef.options, valueId);
        if (!pricing) continue;
        const optionLabel = readSpecOptionLabel(specDef.options, valueId);
        const valueLabel = optionLabel ?? valueId;
        const titlePart = specDef.title ? ` (${specDef.title})` : '';
        const parentPart = parentLabel ? ` for ${parentLabel}` : '';
        specs.push({
          key: `${parent.optionId}-${ans.specification}-${valueId}`,
          label: `${valueLabel}${titlePart}${parentPart}`,
          model: pricing.model,
          price: pricing.price,
          invoiceName,
          specValueLabel: valueLabel,
        });
      }
    }
    if (specs.length > 0) {
      const existing = result[parent.optionId] ?? [];
      result[parent.optionId] = [...existing, ...specs];
    }
  };

  for (const cut of response.selectedCuts ?? []) collect(cut, 'Cutsheet');
  for (const t of response.trimSelections?.trim ?? []) collect(t, 'Trim');
  for (const e of response.extraSelections?.extras ?? []) collect(e, 'Extra');

  return result;
}

interface SpecificationAnswer {
  value?: string | number | Array<string | number | null> | null;
}

/**
 * Joins spec answer values with `|` so the label reads `Cut | spec1 | spec2`.
 * @param specs
 */
function formatSpecLabel(
  specs: Array<SpecificationAnswer | null | undefined> | null | undefined,
): string {
  if (!specs?.length) return '';
  const parts: string[] = [];
  for (const spec of specs) {
    if (!spec) continue;
    const v = spec.value;
    if (v == null) continue;
    if (Array.isArray(v)) {
      const joined = v
        .filter((item) => item != null && item !== '')
        .map((item) => String(item))
        .join(', ');
      if (joined) parts.push(joined);
    } else {
      const str = String(v).trim();
      if (str) parts.push(str);
    }
  }
  return parts.join(' | ');
}

interface BuildV2YieldSourcesArgs {
  response: {
    selectedCuts?:
      | Array<
          | {
              optionId: string;
              specifications?: Array<
                SpecificationAnswer | null | undefined
              > | null;
            }
          | null
          | undefined
        >
      | null
      | undefined;
    trimSelections?: {
      trim?:
        | Array<
            | {
                optionId: string;
                specifications?: Array<
                  SpecificationAnswer | null | undefined
                > | null;
              }
            | null
            | undefined
          >
        | null
        | undefined;
    } | null;
    extraSelections?: {
      extras?:
        | Array<
            | {
                optionId: string;
                specifications?: Array<
                  SpecificationAnswer | null | undefined
                > | null;
              }
            | null
            | undefined
          >
        | null
        | undefined;
    } | null;
  };
  optionsById: Map<string, CutsheetCapabilityOptionFragmentFragment>;
  /** Questionnaire whose `pricingOverrides` take precedence over option pricing. */
  questionnaire?: CutsheetQuestionnaireFragmentFragment | null;
}

function buildLabel(base: string, specLabel: string): string {
  return specLabel ? `${base} | ${specLabel}` : base;
}

export function buildV2YieldSources(
  args: BuildV2YieldSourcesArgs,
): YieldSource[] {
  const { response, optionsById, questionnaire } = args;
  const overrides =
    (questionnaire?.pricingOverrides as Record<
      string,
      PricingOverride
    > | null) ?? null;
  const sources: YieldSource[] = [];
  const pushSource = (
    optionId: string,
    fallbackLabel: string,
    specs: Array<SpecificationAnswer | null | undefined> | null | undefined,
  ) => {
    const option = optionsById.get(optionId);
    const resolved = resolveV2OptionPricing(optionId, option, overrides);
    sources.push({
      capabilityOptionId: optionId,
      label: buildLabel(option?.title ?? fallbackLabel, formatSpecLabel(specs)),
      pricing:
        resolved && resolved.price > 0
          ? { model: resolved.model, price: resolved.price }
          : undefined,
    });
  };
  for (const cut of response.selectedCuts ?? []) {
    if (!cut?.optionId) continue;
    pushSource(cut.optionId, 'Cut', cut.specifications);
  }
  for (const t of response.trimSelections?.trim ?? []) {
    if (!t?.optionId) continue;
    pushSource(t.optionId, 'Trim', t.specifications);
  }
  for (const e of response.extraSelections?.extras ?? []) {
    if (!e?.optionId) continue;
    pushSource(e.optionId, 'Extra', e.specifications);
  }
  return sources;
}
